import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Eye, EyeOff, AlertCircle, CheckCircle2 } from 'lucide-react';
import { getAIConfig, saveAIConfig, removeAIApiKey, type AIProvider } from '@/services/ai/ai-config';
import { getAvailableModels } from '@/services/ai/ai-service';
import { showSuccessToast, showErrorToast } from '@/utils/error-handler';

interface AIConfigModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function AIConfigModal({ open, onOpenChange }: AIConfigModalProps) {
  const [apiKey, setApiKey] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [provider, setProvider] = useState<AIProvider>('openai');
  const [model, setModel] = useState('');
  const [autoAnalyze, setAutoAnalyze] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  // Get available models for current provider
  const availableModels = getAvailableModels(provider);

  // Load existing config when modal opens
  useEffect(() => {
    if (open) {
      const config = getAIConfig();
      if (config) {
        setApiKey(config.apiKey);
        setProvider(config.provider);
        const models = getAvailableModels(config.provider);
        const savedModel = config.model || models[0]?.value || '';
        const modelExists = models.some((m) => m.value === savedModel);
        setModel(modelExists ? savedModel : (models[0]?.value || ''));
        setAutoAnalyze(config.autoAnalyze ?? false);
      } else {
        // Reset to defaults
        setApiKey('');
        setProvider('openai');
        const models = getAvailableModels('openai');
        setModel(models[0]?.value || '');
        setAutoAnalyze(false);
      }
      setTestResult(null);
    }
  }, [open]);

  // Update model when provider changes
  useEffect(() => {
    const models = getAvailableModels(provider);
    if (models.length > 0) {
      const currentModelExists = models.find(m => m.value === model);
      if (!currentModelExists) {
        setModel(models[0].value);
      }
    }
  }, [provider]); // Only depend on provider, not model

  const handleSave = async () => {
    if (!apiKey.trim()) {
      showErrorToast('API key is required');
      return;
    }

    setIsSaving(true);
    try {
      saveAIConfig({
        apiKey: apiKey.trim(),
        provider,
        model: model || undefined,
        autoAnalyze
      });
      showSuccessToast('AI configuration saved successfully');
      onOpenChange(false);
    } catch (error) {
      showErrorToast(error, 'Failed to save AI configuration');
    } finally {
      setIsSaving(false);
    }
  };

  const handleTest = async () => {
    if (!apiKey.trim()) {
      setTestResult({ success: false, message: 'Please enter an API key first' });
      return;
    }

    setIsTesting(true);
    setTestResult(null);

    try {
      // Temporarily save config for testing
      const tempConfig = {
        apiKey: apiKey.trim(),
        provider,
        model: model || undefined
      };
      saveAIConfig(tempConfig);

      // Test with a simple completion
      const { complete } = await import('@/services/ai/ai-service');
      const response = await complete(
        'Say "OK" if you can read this.',
        undefined,
        { maxTokens: 10, temperature: 0 }
      );

      if (response.toLowerCase().includes('ok')) {
        setTestResult({ success: true, message: 'API key is valid and working!' });
      } else {
        setTestResult({ success: false, message: 'Unexpected response from API' });
      }
    } catch (error: any) {
      setTestResult({
        success: false,
        message: error.message || 'Failed to connect to AI service'
      });
    } finally {
      setIsTesting(false);
    }
  };

  const handleRemove = () => {
    if (confirm('Are you sure you want to remove your API key? This will disable AI features.')) {
      removeAIApiKey();
      setApiKey('');
      setTestResult(null);
      showSuccessToast('API key removed');
    }
  };

  const isConfigured = getAIConfig() !== null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>AI Configuration</DialogTitle>
          <DialogDescription>
            Configure your AI API key to enable AI features.
          </DialogDescription>
        </DialogHeader>

        {/* Local Storage Explanation */}
        <Alert className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <strong>Local Storage:</strong> Your API key is stored securely in your browser's localStorage and never sent to our servers. 
            All AI requests are made directly from your browser to OpenAI/Anthropic. You control your own API keys and usage.
          </AlertDescription>
        </Alert>

        <div className="space-y-4 py-4">
          {/* Provider Selection */}
          <div className="space-y-2">
            <Label htmlFor="provider">AI Provider</Label>
            <Select value={provider} onValueChange={(value) => setProvider(value as AIProvider)}>
              <SelectTrigger id="provider">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="openai">OpenAI (GPT-4, GPT-3.5)</SelectItem>
                <SelectItem value="anthropic">Anthropic (Claude)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Model Selection */}
          <div className="space-y-2">
            <Label htmlFor="model">Model</Label>
            <Select value={model} onValueChange={setModel}>
              <SelectTrigger id="model">
                <SelectValue placeholder="Select a model" />
              </SelectTrigger>
              <SelectContent>
                {availableModels.map((modelOption) => (
                  <SelectItem key={modelOption.value} value={modelOption.value}>
                    <div className="flex flex-col">
                      <span>{modelOption.label}</span>
                      {modelOption.description && (
                        <span className="text-xs text-muted-foreground">{modelOption.description}</span>
                      )}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Choose the AI model to use. Different models have different capabilities and costs.
            </p>
          </div>

          {/* API Key */}
          <div className="space-y-2">
            <Label htmlFor="apiKey">API Key</Label>
            <div className="relative">
              <Input
                id="apiKey"
                type={showApiKey ? 'text' : 'password'}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder={provider === 'openai' ? 'sk-...' : 'sk-ant-...'}
                className="pr-10"
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                onClick={() => setShowApiKey(!showApiKey)}
              >
                {showApiKey ? (
                  <EyeOff className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <Eye className="h-4 w-4 text-muted-foreground" />
                )}
              </Button>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">
                {provider === 'openai' && (
                  <>Get your key at <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">platform.openai.com/api-keys</a></>
                )}
                {provider === 'anthropic' && (
                  <>Get your key at <a href="https://console.anthropic.com/settings/keys" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">console.anthropic.com/settings/keys</a></>
                )}
              </p>
              <p className="text-xs text-muted-foreground">
                <strong>Privacy:</strong> Stored in browser localStorage • Never sent to our servers • Direct API calls from your browser
              </p>
            </div>
          </div>

          {/* Auto-Analyze Setting */}
          <div className="space-y-3 p-4 border rounded-lg bg-muted/30">
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="autoAnalyze"
                checked={autoAnalyze}
                onChange={(e) => setAutoAnalyze(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer"
              />
              <div className="flex-1 space-y-1">
                <Label htmlFor="autoAnalyze" className="text-sm font-medium cursor-pointer">
                  Auto-Analyze Results
                </Label>
                <p className="text-xs text-muted-foreground">
                  Automatically generate AI explanations, detect patterns, and suggest next analyses when results are available.
                  This will use your API credits automatically.
                </p>
              </div>
            </div>
          </div>


          {/* Test Result */}
          {testResult && (
            <Alert variant={testResult.success ? 'default' : 'destructive'}>
              <div className="flex items-start gap-2">
                {testResult.success ? (
                  <CheckCircle2 className="h-4 w-4 mt-0.5 text-green-600" />
                ) : (
                  <AlertCircle className="h-4 w-4 mt-0.5" />
                )}
                <AlertDescription>{testResult.message}</AlertDescription>
              </div>
            </Alert>
          )}

          {/* Actions */}
          <div className="flex items-center justify-between gap-2 pt-2">
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handleTest}
                disabled={isTesting || !apiKey.trim()}
              >
                {isTesting ? 'Testing...' : 'Test Connection'}
              </Button>
              {isConfigured && (
                <Button
                  variant="outline"
                  onClick={handleRemove}
                  className="text-destructive hover:text-destructive"
                >
                  Remove
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={isSaving || !apiKey.trim()}>
                {isSaving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
